import {difference} from 'lodash';
import React, {Fragment, ReactNode} from 'react';
import styled from 'styled-components';

import {Renderer} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {InterfaceEventsEnum} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {Icon, IconName} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {ellipsis, flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {UserTheme} from '../../../../../libs/shared/ui/src/theme/themeTypes';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {renderTagIcon} from '../../../../components/common/dropdowns/dropdownItemTagIcon';
import {FilterDropdownRenderer} from '../../../../components/common/filters/moreFiltersDropdown/moreFiltersDropdown';
import {AnalyticsFilterTypesEnum} from '../../../../models/api/analyticsSavedViewModel';
import {TagModel} from '../../../../models/api/tagModel';
import {useAnalyticsTracking} from '../../../helpers/tracking/useAnalyticsTracking';

/*
 * Types.
 */

export type AnalyticsFilterItem = {
  isActive: boolean;
  id: string;
  title: ReactNode;
  renderDropdown: FilterDropdownRenderer;
  renderButton: Renderer;
  type: AnalyticsFilterTypesEnum;
  isDisabled?: boolean;
  rightIcon?: ReactNode;
};

/*
 * Styles.
 */

const StyledWrapper = styled.div`
  ${flexCenter}
`;

const StyledIconDiv = styled.div`
  margin-right: 8px;
`;

const StyledValue = styled.div`
  ${ellipsis()};
`;

const StyledTagIconDiv = styled.div`
  display: inline-block;
  vertical-align: middle;
  position: relative;
  bottom: 1px;
`;

/*
 * Constants.
 */

export const analyticsFilterColor = {
  findBase: (theme: UserTheme) => theme.greys.shade80,
  findActive: (theme: UserTheme) => theme.palette.blue.shade40,
};

/*
 * Helpers.
 */

export const renderFilterTitle = (name: ReactNode, iconName: IconName) => (
  <UserThemeConsumer>
    {(theme) => (
      <StyledWrapper>
        <StyledIconDiv>
          <Icon name={iconName} color={theme.greys.shade70} />
        </StyledIconDiv>
        <StyledValue>{name}</StyledValue>
      </StyledWrapper>
    )}
  </UserThemeConsumer>
);

export const useFiltersTracking = (type: AnalyticsFilterTypesEnum) => {
  const trackAnalytics = useAnalyticsTracking();

  const trackOnRemove = () => {
    trackAnalytics(InterfaceEventsEnum.ANALYTICS_FILTER_REMOVE, {
      filter: type,
    });
  };

  const trackOnEdit = (
    newItems: ReadonlyArray<number | string>,
    previousItems?: ReadonlyArray<number | string>,
    extraFields?: object,
  ) => {
    const previouslySelectedItems = previousItems ?? [];
    const additions = difference(newItems, previouslySelectedItems).length;
    const deletions = difference(previouslySelectedItems, newItems).length;

    trackAnalytics(InterfaceEventsEnum.ANALYTICS_FILTER_EDIT, {
      filter: type,
      additions,
      deletions,
      remaining: newItems.length,
      ...extraFields,
    });
  };

  return {trackOnRemove, trackOnEdit};
};

export const renderTagButtonItem = (tag: TagModel) => (
  <Fragment key={tag.id}>
    <StyledTagIconDiv>{renderTagIcon(tag, {isSmall: true})}</StyledTagIconDiv> {tag.name}
  </Fragment>
);
